import { t, useStoreLanguage } from "./features/store-language/i18n.ts";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import ShopLayout from "./Layout/ShopLayout.tsx";
import CustomerLogin from "./components/Shop/Auth/CustomerLogin.tsx";
import AgencyInvitationPage from "./components/Shop/Auth/AgencyInvitationPage.tsx";
import AgencyRegistrationPage from "./components/Shop/Auth/AgencyRegistrationPage.tsx";
import AgencyLoginPage from "./components/Shop/Auth/AgencyLoginPage.tsx";
import AgencyPage from "./components/Shop/Agency/AgencyPage.tsx";
import ShopCategories from "./components/Shop/Category/ShopCategories.tsx";
import SchemaList from "./components/Shop/Schema/SchemaList.tsx";
import SchemaPreview from "./components/Shop/Schema/SchemaPreview.tsx";
import CustomerPanelLayout from "./components/Shop/CustomerPanel/CustomerPanelLayout.tsx";
import { CustomerProfilePage } from "./components/Shop/Profile/CustomerProfile.tsx";
import OrdersPage from "./components/Shop/Orders/OrdersPage.tsx";
import UserProductFavorites from "./components/Shop/Favorites/UserProductFavorites.tsx";
import OrderDetailPage from "./components/Shop/Orders/OrderDetailPage.tsx";
import NotFoundPage from "./components/shared/NotFoundPage.jsx";
import RajLandingPage from "./features/raj-landing/RajLandingPage.tsx";
import { isRajMarketingHost } from "./features/raj-landing/rajMarketingDomain.ts";

const PlatformAdminApp = lazy(() => { return import("./features/platform-admin/PlatformAdminApp.tsx"); });

function LegacyStorefrontRedirect() {
  const location = useLocation();
  let pathname = location.pathname.replace(/^\/shop(?=\/|$)/, "") || "/";
  const legacyProduct = /^\/product\/(\d+)\/(.+)$/.exec(pathname);
  if (legacyProduct) {
    pathname = `/products/${legacyProduct[2]}-p-${legacyProduct[1]}`;
  } else if (pathname === "/my/basket" || pathname === "/basket") {
    pathname = "/account/orders";
  } else if (pathname === "/my") {
    pathname = "/account/orders";
  } else if (pathname === "/my/orders") {
    pathname = "/account/orders";
  } else if (pathname === "/my/favorites") {
    pathname = "/account/favorites";
  } else if (/^\/my\/order\/[^/]+(?:\/edit)?$/.test(pathname)) {
    pathname = pathname.replace(/^\/my\/order\/([^/]+)(?:\/edit)?$/, "/account/orders/$1");
  } else if (pathname === "/profile") {
    pathname = "/account/orders";
  } else if (pathname === "/orders" || pathname.startsWith("/order/")) {
    pathname = "/account/orders";
  } else if (pathname.toLocaleLowerCase() === "/category/all") {
    pathname = "/products";
  } else {
    pathname = pathname.replace(/^\/category(?=\/|$)/, "/categories");
  }
  return (
    <Navigate
      to={`${pathname}${location.search}${location.hash}`}
      replace
    />
  );
}

function CatalogueHome() {
  const location = useLocation();
  // A fresh catalogue navigation resets filters; browser Back still restores its snapshot.
  return (
    <SchemaList key={`home:${location.key}`} />
  );
}

export default function ShopRoutes() {
  useStoreLanguage();
  const { pathname } = useLocation();
  const isAdminRoute = pathname === "/admin" || pathname.startsWith("/admin/");
  if (isRajMarketingHost() && !isAdminRoute) {
    return (
      <Routes>
        <Route
          path="/"
          element={<RajLandingPage />}
        />
        <Route
          path="*"
          element={<NotFoundPage />}
        />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route
        path="/raj"
        element={<NotFoundPage />}
      />
      <Route path="/admin/login" element={<CustomerLogin />} />
      <Route
        path="/admin/*"
        element={
          <Suspense fallback={<p role="status">{t("در حال بارگذاری مدیریت مرکزی…")}</p>}>
            <PlatformAdminApp />
          </Suspense>
        }
      />
      <Route path="/invitation" element={<AgencyInvitationPage />} />
      <Route
        path="/register"
        element={<AgencyRegistrationPage />}
      />
      <Route path="/login" element={<AgencyLoginPage />} />
      <Route path="/agency" element={<Navigate to="/account/personnel" replace />} />
      <Route path="/agency/login" element={<Navigate to="/login" replace />} />
      <Route path="/customer/login" element={<Navigate to="/admin/login" replace />} />
      <Route path="/customer/register" element={<Navigate to="/login" replace />} />
      <Route path="/*" element={<ShopLayout />}>
        <Route path="showroom/*" element={null} />
        <Route index element={<CatalogueHome />} />
        <Route path="categories" element={<ShopCategories />} />
        <Route path="categories/:slug" element={<SchemaList />} />
        <Route path="products" element={<SchemaList />} />
        <Route
          path="products/:productKey/variants/:variantId"
          element={<SchemaPreview />}
        />
        <Route
          path="products/:productKey/:variantTitle"
          element={<SchemaPreview />}
        />
        <Route path="products/:productKey" element={<SchemaPreview />} />
        <Route path="search" element={<SchemaList />} />
        <Route
          path="schemaId/:schemaId/color/:color"
          element={<Navigate to="/search?legacyLink=1" replace />}
        />
        <Route path=":slug/schema/:schemaName" element={<SchemaPreview />} />
        <Route path="schema/:schemaName" element={<SchemaPreview />} />
        <Route
          path="orders"
          element={<Navigate to="/account/orders" replace />}
        />
        <Route
          path="order/:id"
          element={<Navigate to="/account/orders" replace />}
        />
        <Route
          path="order/:demandId/edit"
          element={<Navigate to="/account/orders" replace />}
        />
        <Route
          path="basket"
          element={<Navigate to="/account/orders" replace />}
        />
        <Route path="cart" element={<Navigate to="/account/orders" replace />} />
        <Route element={<CustomerPanelLayout />}>
          <Route path="account" element={<Navigate to="/account/orders" replace />} />
          <Route path="account/orders" element={<OrdersPage />} />
          <Route path="account/profile" element={<CustomerProfilePage />} />
          <Route path="account/favorites" element={<UserProductFavorites />} />
          <Route path="account/orders/create" element={<OrderDetailPage />} />
          <Route path="account/orders/:id" element={<OrderDetailPage />} />
          <Route path="account/personnel" element={<AgencyPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="/categories/all" element={<Navigate to="/products" replace />} />
      <Route path="/shop/*" element={<LegacyStorefrontRedirect />} />
      <Route path="/category/*" element={<LegacyStorefrontRedirect />} />
      <Route path="/product/*" element={<LegacyStorefrontRedirect />} />
      <Route path="/my/*" element={<LegacyStorefrontRedirect />} />
      <Route path="/profile" element={<LegacyStorefrontRedirect />} />
      <Route path="/basket" element={<LegacyStorefrontRedirect />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
